import { useRouter } from 'next/router'
import { useMemo, useEffect, useState } from 'react'
import enTranslations from '../locales/en.json'
import frTranslations from '../locales/fr.json'
import esTranslations from '../locales/es.json'
import plTranslations from '../locales/pl.json'
import arTranslations from '../locales/ar.json'
import zhTranslations from '../locales/zh.json'

const translations = {
  en: enTranslations,
  fr: frTranslations,
  es: esTranslations,
  pl: plTranslations,
  ar: arTranslations,
  zh: zhTranslations,
}

export function useTranslation() {
  const router = useRouter()
  const { locale, defaultLocale } = router
  const [detectedLocale, setDetectedLocale] = useState(locale || defaultLocale)

  // Detect browser language on client side without changing URL
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const browserLang = navigator.language || navigator.userLanguage
      let browserLocale = 'en' // default fallback

      // Map browser language codes to our supported locales
      if (browserLang.startsWith('fr')) {
        browserLocale = 'fr'
      } else if (browserLang.startsWith('es')) {
        browserLocale = 'es'
      } else if (browserLang.startsWith('pl')) {
        browserLocale = 'pl'
      } else if (browserLang.startsWith('ar')) {
        browserLocale = 'ar'
      } else if (browserLang.startsWith('zh')) {
        browserLocale = 'zh'
      }

      // Set detected locale without changing URL
      if (browserLocale !== detectedLocale) {
        setDetectedLocale(browserLocale)
      }
    }
  }, [detectedLocale])

  // Use detected locale for translations, fallback to router locale
  const activeLocale = detectedLocale || locale || defaultLocale

  const t = useMemo(
    () =>
      (key, variables = {}) => {
        const keys = key.split('.')
        let value =
          translations[activeLocale] ||
          translations[defaultLocale] ||
          translations.en

        for (const k of keys) {
          if (value && typeof value === 'object' && k in value) {
            value = value[k]
          } else {
            // Fallback to default locale
            value = translations[defaultLocale] || translations.en
            for (const fallbackKey of keys) {
              if (value && typeof value === 'object' && fallbackKey in value) {
                value = value[fallbackKey]
              } else {
                return key
              }
            }
          }
        }

        // Handle interpolation if value is a string and variables are provided
        if (typeof value === 'string' && Object.keys(variables).length > 0) {
          return value.replace(/\{\{(\w+)\}\}/g, (match, variableName) =>
            variables[variableName] !== undefined
              ? variables[variableName]
              : match
          )
        }

        return value || key
      },
    [activeLocale, defaultLocale]
  )

  return { t, locale: activeLocale, defaultLocale }
}

export function getLocaleFromPath(pathname) {
  const segments = pathname.split('/')
  return segments[1] || 'en'
}

export function getPathWithoutLocale(pathname) {
  const segments = pathname.split('/')
  return segments.slice(2).join('/') || ''
}

export function getLocalizedPath(path, locale) {
  if (locale === 'en') {
    return path
  }
  return `/${locale}${path}`
}
